/**
 * The board's chart chrome (dashboard design rules §4.4 / §5 / §6): an
 * outlined card with a title, an optional `Freshness` indicator, and the
 * chart's **table twin** — a "View data" affordance that opens the exact
 * values behind the chart as a `DataGrid` in a `Drawer`, so every charted
 * value is also reachable as a table without demoting the board to tables.
 *
 * The open table twin lives in the URL (`?data=<id>`, via `useDrawerParam`),
 * so it is shareable and the back button closes it.
 */

import { Icon, Table2 } from "@norican/icons";
import {
  Button,
  Card,
  DataGrid,
  type DataGridColumnDef,
  Drawer,
  Freshness,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";

import { useDrawerParam } from "@/lib/drill";

import { type Verdict, VerdictFooter } from "./verdict";

/** URL search param carrying which chart's table twin is open. */
export const TABLE_TWIN_PARAM = "data";

export interface ChartCardTableTwin<Row> {
  /**
   * Board-unique id for this chart's twin — lands in the URL as `?data=<id>`
   * (e.g. `"cost-trend"`).
   */
  id: string;
  /** Grid accessible name, e.g. "Cost per subscription, last 30 days". */
  ariaLabel: string;
  /** The rows behind the chart — same slice, same numbers. */
  data: Row[];
  columns: DataGridColumnDef<Row>[];
  getRowId: (row: Row) => string;
  /** Drawer heading. Defaults to the card title. */
  title?: string;
}

export interface ChartCardProps<Row> {
  /** Card heading — with one series and no legend, this names the series. */
  title: string;
  /** Muted line beside the heading — a count, a scope, a coverage caveat. */
  caption?: ReactNode;
  /** `collected_at` of the data on show; renders a `Freshness` dot. */
  since?: string | number | Date | null;
  /** Staleness threshold in ms for the `Freshness` tone escalation. */
  staleAfterMs?: number;
  /** Extra header controls (e.g. a legend toggle). Rendered before View data. */
  actions?: ReactNode;
  /** The chart's table twin; omit only when the chart sits next to its table. */
  tableTwin?: ChartCardTableTwin<Row>;
  /**
   * Closing status verdict, rendered in the card footer — the executive deck's
   * card grammar (`components/board/verdict.tsx`). One short sentence saying
   * what the chart means, not what it plots.
   */
  verdict?: Verdict;
  /** The chart itself (a `Chart.Container` with an `aria-label`). */
  children: ReactNode;
}

export function ChartCard<Row>({
  title,
  caption,
  since,
  staleAfterMs,
  actions,
  tableTwin,
  verdict,
  children,
}: ChartCardProps<Row>) {
  const drawer = useDrawerParam(TABLE_TWIN_PARAM);
  const twinOpen = tableTwin != null && drawer.value === tableTwin.id;

  return (
    <Card variant="outline">
      <Card.Header>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography variant="heading-4" as="h2">
            {title}
          </Typography>
          <div className="flex flex-wrap items-center gap-3">
            {caption && (
              <Typography variant="body-sm" tone="muted">
                {caption}
              </Typography>
            )}
            {since != null && <Freshness label="Data" since={since} staleAfter={staleAfterMs} />}
            {actions}
            {tableTwin && (
              <Button
                variant="ghost"
                size="sm"
                startIcon={<Icon icon={Table2} size="sm" />}
                onClick={() => drawer.open(tableTwin.id)}
              >
                View data
              </Button>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body>{children}</Card.Body>
      {verdict && (
        <Card.Footer>
          <VerdictFooter verdict={verdict} />
        </Card.Footer>
      )}
      {tableTwin && (
        <Drawer.Root
          side="right"
          open={twinOpen}
          onOpenChange={(open) => {
            if (!open) {
              drawer.close();
            }
          }}
        >
          <Drawer.Portal>
            <Drawer.Backdrop />
            <Drawer.Viewport>
              <Drawer.Popup>
                <Drawer.Header>
                  <Drawer.Title>{tableTwin.title ?? title}</Drawer.Title>
                  <Drawer.CloseButton />
                </Drawer.Header>
                <Drawer.Body>
                  <DataGrid<Row>
                    aria-label={tableTwin.ariaLabel}
                    data={tableTwin.data}
                    columns={tableTwin.columns}
                    getRowId={tableTwin.getRowId}
                    density="compact"
                  />
                </Drawer.Body>
              </Drawer.Popup>
            </Drawer.Viewport>
          </Drawer.Portal>
        </Drawer.Root>
      )}
    </Card>
  );
}
